/**
 * CSV Import Service
 * Mirrors the Data Ingestion Service API defined in docs/CSV_IMPORT_DESIGN.md
 *
 * Base path: /api/v1/data-ingestion/csv-imports
 *
 * All backend responses are wrapped in the standard envelope:
 *   { statusCode, message, traceMessage, success, responseData: T }
 * Every method unwraps responseData before returning.
 */
import type {
  CsvImport,
  CsvPreview,
  MappingRecommendationsResponse,
  MappingTemplate,
  ValidationResult,
} from "../../types";
import axiosClient from "../axiosClient";

// ── Standard response envelope ────────────────────────────────────────────────

interface ApiEnvelope<T> {
  statusCode: number;
  message: string;
  traceMessage: string;
  success: boolean;
  responseData: T;
}

// ── Payload types ─────────────────────────────────────────────────────────────

export interface UploadCsvPayload {
  file: File;
  agency_id: string;
  client_id: string;
  created_by?: string;
}

export interface ListCsvImportsParams {
  agency_id: string;
  client_id: string;
}

// ── Upload response shape from backend (partial — augmented below) ────────────

interface UploadResponseData {
  csv_import_id: string;
  status: string;
  original_filename: string;
  file_size_bytes: number;
  row_count: number | null;
  column_headers: string[];
  csv_signature: string | null;
  s3_key: string;
  created_at: string;
  /** Included when backend is updated; may be absent on older builds. */
  agency_id?: string;
  client_id?: string;
  created_by?: string;
}

export const csvImportService = {
  // POST /api/v1/data-ingestion/csv-imports
  // X-T-Id is sent explicitly so the gateway can inject the tenant context for
  // platform admins (whose JWT omits t_id). For agency/org users the JWT-derived
  // value takes priority (gateway overwrites the header with claims_to_headers).
  upload: async (payload: UploadCsvPayload): Promise<CsvImport> => {
    const form = new FormData();
    form.append("file", payload.file);
    form.append("agency_id", payload.agency_id);
    form.append("client_id", payload.client_id);
    if (payload.created_by) {
      form.append("created_by", payload.created_by);
    }
    const { data } = await axiosClient.post<ApiEnvelope<UploadResponseData>>(
      "/data-ingestion/csv-imports",
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-T-Id": payload.agency_id,
        },
      },
    );
    const rd = data.responseData;
    // Augment fields the backend upload response omits — use payload as source of truth
    return {
      csv_import_id: rd.csv_import_id,
      status: rd.status as CsvImport["status"],
      original_filename: rd.original_filename,
      row_count: rd.row_count,
      column_headers: rd.column_headers,
      csv_signature: rd.csv_signature,
      mapping_template_id: null,
      applied_mapping: null,
      can_ingest: null,
      ingested_row_count: null,
      report_id: null,
      mart_build_status: null,
      date_from: null,
      date_to: null,
      error_message: null,
      agency_id: rd.agency_id ?? payload.agency_id,
      client_id: rd.client_id ?? payload.client_id,
      created_by: rd.created_by ?? payload.created_by ?? "system",
      created_at: rd.created_at,
      updated_at: rd.created_at,
    };
  },

  // PUT /api/v1/data-ingestion/csv-imports/{id}/file
  // Replaces the file on an existing import record and resets it to UPLOADED.
  // The same record id is preserved — no new history entry is created.
  replaceFile: async (
    id: string,
    file: File,
    agencyId: string,
  ): Promise<CsvImport> => {
    const form = new FormData();
    form.append("file", file);
    form.append("agency_id", agencyId);
    const { data } = await axiosClient.put<ApiEnvelope<UploadResponseData>>(
      `/data-ingestion/csv-imports/${id}/file`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-T-Id": agencyId,
        },
      },
    );
    const rd = data.responseData;
    return {
      csv_import_id: rd.csv_import_id,
      status: rd.status as CsvImport["status"],
      original_filename: rd.original_filename,
      row_count: rd.row_count,
      column_headers: rd.column_headers,
      csv_signature: rd.csv_signature,
      mapping_template_id: null,
      applied_mapping: null,
      can_ingest: null,
      ingested_row_count: null,
      report_id: null,
      mart_build_status: null,
      date_from: null,
      date_to: null,
      error_message: null,
      agency_id: rd.agency_id ?? agencyId,
      client_id: rd.client_id ?? "",
      created_by: rd.created_by ?? "system",
      created_at: rd.created_at,
      updated_at: rd.created_at,
    };
  },

  // GET /api/v1/data-ingestion/csv-imports?agency_id=x&client_id=y
  list: async (params: ListCsvImportsParams): Promise<CsvImport[]> => {
    const { data } = await axiosClient.get<ApiEnvelope<{ items: CsvImport[] }>>(
      "/data-ingestion/csv-imports",
      { params, headers: { "X-T-Id": params.agency_id } },
    );
    return data.responseData.items ?? [];
  },

  // GET /api/v1/data-ingestion/csv-imports/{id}
  getById: async (id: string, agencyId: string): Promise<CsvImport> => {
    const { data } = await axiosClient.get<ApiEnvelope<CsvImport>>(
      `/data-ingestion/csv-imports/${id}`,
      { params: { agency_id: agencyId }, headers: { "X-T-Id": agencyId } },
    );
    return data.responseData;
  },

  // GET /api/v1/data-ingestion/csv-imports/{id}/preview?rows=20
  preview: async (
    id: string,
    agencyId: string,
    rows = 20,
  ): Promise<CsvPreview> => {
    const { data } = await axiosClient.get<ApiEnvelope<CsvPreview>>(
      `/data-ingestion/csv-imports/${id}/preview`,
      {
        params: { agency_id: agencyId, rows },
        headers: { "X-T-Id": agencyId },
      },
    );
    return data.responseData;
  },

  // GET /api/v1/data-ingestion/csv-imports/{id}/mapping-recommendations
  getMappingRecommendations: async (
    id: string,
    agencyId: string,
  ): Promise<MappingRecommendationsResponse> => {
    const { data } = await axiosClient.get<
      ApiEnvelope<MappingRecommendationsResponse>
    >(`/data-ingestion/csv-imports/${id}/mapping-recommendations`, {
      params: { agency_id: agencyId },
      headers: { "X-T-Id": agencyId },
    });
    return data.responseData;
  },

  // POST /api/v1/data-ingestion/csv-imports/{id}/mapping
  confirmMapping: async (
    id: string,
    agencyId: string,
    mapping: Record<string, string>,
    saveAsTemplate = false,
    templateName?: string,
  ): Promise<{
    status: string;
    template_saved: boolean;
    template_id: string | null;
    validation_preview: {
      can_ingest: boolean;
      missing_flagged_fields: string[];
    };
  }> => {
    const { data } = await axiosClient.post<
      ApiEnvelope<{
        status: string;
        template_saved: boolean;
        template_id: string | null;
        validation_preview: {
          can_ingest: boolean;
          missing_flagged_fields: string[];
        };
      }>
    >(
      `/data-ingestion/csv-imports/${id}/mapping`,
      {
        agency_id: agencyId,
        mapping,
        save_as_template: saveAsTemplate,
        template_name: templateName ?? null,
      },
      { headers: { "X-T-Id": agencyId } },
    );
    return data.responseData;
  },

  // GET /api/v1/data-ingestion/csv-imports/{id}/validation
  validate: async (id: string, agencyId: string): Promise<ValidationResult> => {
    const { data } = await axiosClient.get<ApiEnvelope<ValidationResult>>(
      `/data-ingestion/csv-imports/${id}/validation`,
      { params: { agency_id: agencyId }, headers: { "X-T-Id": agencyId } },
    );
    return data.responseData;
  },

  // POST /api/v1/data-ingestion/csv-imports/{id}/ingest
  ingest: async (
    id: string,
    agencyId: string,
  ): Promise<{
    csv_import_id: string;
    status: string;
    ingested_row_count: number;
    bq_table: string;
    message: string;
    date_from: string | null;
    date_to: string | null;
  }> => {
    const { data } = await axiosClient.post<
      ApiEnvelope<{
        csv_import_id: string;
        status: string;
        ingested_row_count: number;
        bq_table: string;
        message: string;
        date_from: string | null;
        date_to: string | null;
      }>
    >(
      `/data-ingestion/csv-imports/${id}/ingest`,
      { agency_id: agencyId },
      { headers: { "X-T-Id": agencyId } },
    );
    return data.responseData;
  },

  // GET /api/v1/data-ingestion/csv-imports/templates?agency_id=x&client_id=y
  listTemplates: async (
    agencyId?: string,
    clientId?: string,
  ): Promise<MappingTemplate[]> => {
    const params: Record<string, string> = {};
    if (agencyId) params["agency_id"] = agencyId;
    if (clientId) params["client_id"] = clientId;
    const { data } = await axiosClient.get<
      ApiEnvelope<{ templates: MappingTemplate[] }>
    >("/data-ingestion/csv-imports/templates", {
      params,
      headers: agencyId ? { "X-T-Id": agencyId } : {},
    });
    return data.responseData.templates ?? [];
  },

  // DELETE /api/v1/data-ingestion/csv-imports/{id}
  // NOTE: dev/testing only — remove before production.
  deleteCsvImport: async (id: string, agencyId: string): Promise<void> => {
    await axiosClient.delete(`/data-ingestion/csv-imports/${id}`, {
      params: { agency_id: agencyId },
      headers: { "X-T-Id": agencyId },
    });
  },

  // PATCH /api/v1/data-ingestion/csv-imports/{id}/report-generated
  // Transitions the import to REPORT_GENERATED and stores the linked report_id.
  // Called immediately after the Report Orchestration Service accepts the
  // shadow report creation request (202 Accepted).
  markReportGenerated: async (
    id: string,
    reportId: string,
    agencyId: string,
  ): Promise<void> => {
    await axiosClient.patch(
      `/data-ingestion/csv-imports/${id}/report-generated`,
      { report_id: reportId },
      { headers: { "X-T-Id": agencyId } },
    );
  },
};
